import { useEffect, useRef, useState } from "react";
import { useAhaCelebration } from "@/contexts/AhaCelebrationContext";
import { ArrowLeft, Send, Loader2, ShieldCheck } from "lucide-react";
import { useMessages, useSendMessage, useThreads } from "@/lib/chat";
import { formatApiErrorDetail } from "@/lib/api";
import ChatSafetyHeader from "@/components/ChatSafetyHeader";

export default function ChatThreadView({ threadId, onBack }) {
  const { data: messages, isLoading } = useMessages(threadId);
  const { triggerAhaCheck } = useAhaCelebration();

  // Phase 7: trigger aha celebration on entering a chat thread.
  useEffect(() => {
    triggerAhaCheck("first_chat");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const { data: threads } = useThreads();
  const send = useSendMessage(threadId);
  const [text, setText] = useState("");
  const [err, setErr] = useState("");
  const scrollRef = useRef(null);

  const thread = (threads || []).find((t) => t.thread_id === threadId);
  const otherName = thread?.other_participant?.display_name || "Conversation";

  // Auto-scroll to bottom when messages length changes
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages?.length]);

  function scrollToSafetyHeader() {
    const el = scrollRef.current;
    if (el) el.scrollTo({ top: 0, behavior: "smooth" });
  }

  async function handleSend(e) {
    e.preventDefault();
    const value = text.trim();
    if (!value) return;
    setErr("");
    setText("");
    try {
      await send.mutateAsync(value);
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      setText(value);
    }
  }

  return (
    <div data-testid="chat-thread-view" className="flex flex-col h-full min-h-0 w-full">
      {/* Top bar */}
      <header className="flex items-center gap-3 px-4 sm:px-6 py-3 border-b border-white/10 bg-black/95">
        <button
          type="button"
          onClick={onBack}
          data-testid="thread-back-button"
          className="md:hidden h-9 w-9 rounded-md flex items-center justify-center hover:bg-white/5"
        >
          <ArrowLeft className="h-4 w-4 text-[#A1A1AA]" />
        </button>
        <span className="h-9 w-9 rounded-full bg-gradient-to-br from-[#E5B83A] to-[#C9971C] text-black font-bold text-sm flex items-center justify-center shrink-0">
          {(otherName || "?").charAt(0).toUpperCase()}
        </span>
        <h2
          data-testid="thread-other-name"
          className="font-display text-base font-semibold text-white truncate flex-1"
        >
          {otherName}
        </h2>
        <button
          type="button"
          onClick={scrollToSafetyHeader}
          aria-label="Safety info and meet location"
          data-testid="chat-safety-jump-button"
          className="h-9 w-9 rounded-md flex items-center justify-center text-[#E5B83A] hover:bg-white/5 transition-colors shrink-0"
        >
          <ShieldCheck className="h-4 w-4" />
        </button>
      </header>

      {/* Safety stack — pinned OUTSIDE the message scroll container so it stays
          visible regardless of auto-scroll-to-bottom on thread open. */}
      <div className="px-4 sm:px-6 pt-4 shrink-0">
        <ChatSafetyHeader />
      </div>

      {/* Message list */}
      <div
        ref={scrollRef}
        data-testid="message-list"
        className="flex-1 min-h-0 overflow-y-auto px-4 sm:px-6 py-6 space-y-2.5"
      >
        {isLoading ? (
          <p className="text-[#A1A1AA] text-sm font-body text-center">Loading…</p>
        ) : !messages || messages.length === 0 ? (
          <p
            data-testid="message-empty"
            className="text-[#A1A1AA] text-sm font-body text-center mt-8"
          >
            No messages yet. Say hi 👋
          </p>
        ) : (
          messages.map((m) => <MessageBubble key={m.message_id} m={m} />)
        )}
      </div>

      {/* Input bar */}
      <form
        onSubmit={handleSend}
        data-testid="message-input-form"
        className="border-t border-white/10 bg-black"
        style={{
          paddingBottom: "max(12px, env(safe-area-inset-bottom))",
        }}
      >
        {err ? (
          <p
            data-testid="message-input-error"
            className="text-xs text-[#FF6B6B] font-body px-4 sm:px-6 pt-2"
          >
            {err}
          </p>
        ) : null}
        <div className="flex items-end gap-2 px-4 sm:px-6 py-3">
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleSend(e);
              }
            }}
            data-testid="message-input"
            rows={1}
            maxLength={2000}
            placeholder={`Message ${otherName}…`}
            className="flex-1 min-h-[40px] max-h-32 resize-none bg-[#121212] border border-white/10 rounded-md px-3 py-2 text-white placeholder:text-white/30 font-body text-sm focus:outline-none focus:ring-1 focus:ring-[#E5B83A] focus:border-[#E5B83A]"
          />
          <button
            type="submit"
            disabled={!text.trim() || send.isPending}
            data-testid="message-send-button"
            className="h-10 w-10 rounded-md bg-gradient-to-r from-[#E5B83A] to-[#C9971C] text-black flex items-center justify-center hover:opacity-95 disabled:opacity-40 disabled:cursor-not-allowed transition-opacity shrink-0"
          >
            {send.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Send className="h-4 w-4" />
            )}
          </button>
        </div>
      </form>
    </div>
  );
}

function MessageBubble({ m }) {
  const time = m.created_at
    ? new Date(m.created_at).toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      })
    : "";
  return (
    <div
      data-testid={m.mine ? "msg-mine" : "msg-theirs"}
      className={"flex " + (m.mine ? "justify-end" : "justify-start")}
    >
      <div className="max-w-[78%] flex flex-col gap-0.5">
        <div
          className={
            "rounded-2xl px-4 py-2 font-body text-sm whitespace-pre-wrap break-words " +
            (m.mine
              ? "bg-[#E5B83A] text-black rounded-br-md"
              : "bg-[#1a1a1a] text-white rounded-bl-md border border-white/5")
          }
        >
          {m.text}
        </div>
        <span
          className={
            "text-[10px] text-[#A1A1AA]/70 font-body px-1 " +
            (m.mine ? "text-right" : "text-left")
          }
        >
          {time}
        </span>
      </div>
    </div>
  );
}
